import {
  createContext,
  useCallback,
  useContext,
  useId,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';

interface RadioGroupCtx {
  name: string;
  value: string | null;
  onValueChange: (value: string) => void;
  disabled: boolean;
  invalid: boolean;
  size: 'sm' | 'md' | 'lg';
}

const RadioGroupContext = createContext<RadioGroupCtx | null>(null);

export interface RadioGroupProps {
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string) => void;
  name?: string;
  label?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  children: ReactNode;
}

/** Container that wires a name + shared value to `<Radio>` children.
 *  Uses native `<input type="radio">` with `name` attribute for
 *  browser-managed exclusivity (also wins on form submit + a11y tree).
 *  Keyboard arrow-nav between radios is delegated to the browser's
 *  built-in radio-group behavior (standard and consistent). */
export function RadioGroup(props: RadioGroupProps): ReactNode {
  const {
    value: controlledValue,
    defaultValue = null,
    onValueChange,
    name,
    label,
    disabled = false,
    invalid = false,
    size = 'md',
    orientation = 'vertical',
    className,
    children,
  } = props;

  const reactName = useId();
  const resolvedName = name ?? reactName;

  const isControlled = controlledValue !== undefined;
  const [uncontrolled, setUncontrolled] = useState<string | null>(defaultValue);
  const value = isControlled ? (controlledValue ?? null) : uncontrolled;

  const handleChange = useCallback(
    (next: string): void => {
      if (!isControlled) setUncontrolled(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const ctx: RadioGroupCtx = {
    name: resolvedName,
    value,
    onValueChange: handleChange,
    disabled,
    invalid,
    size,
  };

  const classes = ['vq-radio-group', className].filter(Boolean).join(' ');

  return (
    <RadioGroupContext.Provider value={ctx}>
      <div
        className={classes}
        data-component="RadioGroup"
        data-orientation={orientation}
        data-size={size}
        data-state={disabled ? 'disabled' : invalid ? 'invalid' : 'idle'}
        role="radiogroup"
        aria-label={label}
        aria-invalid={invalid || undefined}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

export interface RadioProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'className' | 'onChange' | 'size' | 'name' | 'checked' | 'value' | 'defaultChecked'
> {
  value: string;
  label?: ReactNode;
  className?: string;
  ref?: Ref<HTMLInputElement>;
  /** Standalone (no RadioGroup): receive raw change events and/or emit value. */
  onCheckedChange?: (checked: boolean) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Required when not inside a RadioGroup — otherwise the group supplies it. */
  name?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/** Single radio button — typically a child of `<RadioGroup>`.
 *  Can also be used standalone (provide `name` + manage `checked` yourself). */
export function Radio(props: RadioProps): ReactNode {
  const {
    value,
    label,
    className,
    ref,
    onCheckedChange,
    onChange,
    name: nameProp,
    checked: checkedProp,
    defaultChecked,
    disabled: disabledProp,
    invalid: invalidProp,
    size: sizeProp,
    ...rest
  } = props;

  const group = useContext(RadioGroupContext);
  const name = nameProp ?? group?.name;
  const checked =
    checkedProp !== undefined ? checkedProp : group !== null ? group.value === value : undefined;
  const disabled = disabledProp === true || group?.disabled === true;
  const invalid = invalidProp === true || group?.invalid === true;
  const size = sizeProp ?? group?.size ?? 'md';

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e);
    if (e.target.checked) {
      onCheckedChange?.(true);
      group?.onValueChange(value);
    }
  };

  const stateAttr = disabled
    ? 'disabled'
    : invalid
      ? 'invalid'
      : checked === true
        ? 'checked'
        : 'unchecked';

  const classes = ['vq-radio', className].filter(Boolean).join(' ');

  return (
    <label className={classes} data-component="Radio" data-size={size} data-state={stateAttr}>
      <input
        {...rest}
        ref={ref}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={handleChange}
      />
      <span className="vq-radio__box" aria-hidden="true" />
      {label !== undefined ? <span className="vq-radio__label">{label}</span> : null}
    </label>
  );
}
